import { useEffect, useState } from "react";
import { UploadSimple, FilePdf, CheckCircle, WarningCircle, X, ArrowsLeftRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import api, { formatApiError } from "../lib/api";
import { fmtDate, toISODate } from "../lib/format";

// Inline-editable document field — plain text input, saves to the record on blur/Enter.
function DocMetaField({ value, placeholder, onSave, testid, tone = "text-stone-500" }) {
  const [v, setV] = useState(value || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => setV(value || ""), [value]);
  const commit = async () => {
    if (v === (value || "")) return;
    setSaving(true);
    try {
      await onSave(v);
    } catch (e) {
      toast.error(formatApiError(e));
      setV(value || "");
    }
    setSaving(false);
  };
  return (
    <input value={v} placeholder={placeholder} disabled={saving}
      onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setV(value || ""); e.currentTarget.blur(); } }}
      onClick={(e) => e.stopPropagation()}
      className={`w-full border-b border-dashed border-stone-200 bg-transparent text-[11px] font-medium ${tone} outline-none transition-colors focus:border-primary/60 placeholder:text-stone-300 placeholder:font-normal`}
      data-testid={testid} />
  );
}

// Airtable lifecycle layout (owner's directory blueprint) — record fields + flags + filed documents per group.
// Document order within a group follows the owner's canonical order (see _doc_order on the backend).
export const GROUPS = [
  { key: "contact", step: 2, title: "Contact & status", desc: "Identity, contact details, emergency contact, status.", flags: [] },
  { key: "hiring", step: 1, title: "Hiring process", desc: "Application, interview notes (incl. face-to-face), references & responses, drug test results, offer letter.", flags: ["references", "drug_test", "drug_test_failed"] },
  { key: "onboarding", title: "Onboarding & orientation", desc: "Hire date, first day worked, orientation.", flags: ["orientation"] },
  { key: "id_passport", step: 3, title: "ID, passport & SS card", desc: "Driver's license, passport, photo ID, and the Social Security card.", flags: ["id_current", "ss_card"] },
  { key: "ss_i9", step: 4, title: "I-9 & SS verification", desc: "The I-9 employment eligibility form and Social Security verification.", flags: ["ss_verify", "i9"] },
  { key: "tb_testing", step: 5, title: "TB — testing & screening", desc: "The TB test itself plus the initial and annual symptom & risk screens.", flags: ["tb_test", "tb_screen"] },
  { key: "tb_screening", title: "TB screening", desc: "Rendered inside the TB card as its own padded block.", flags: [] },
  { key: "residency", step: 6, title: "Residency", desc: "Proof of PA residency and the residency / FBI determination.", flags: ["residency"] },
  { key: "pa_bg", step: 7, title: "PA background check", desc: "Pennsylvania state background checks and attestations.", flags: ["pa_bg"] },
  { key: "fbi", step: 8, title: "FBI", desc: "FBI fingerprinting / IdentoGO when required.", flags: ["fbi"] },
  { key: "certifications", step: 9, title: "Certifications & ongoing training", desc: "CPR/First Aid, competency exams, annual trainings.", flags: [] },
  { key: "employment", step: 10, title: "Employment & separation", desc: "Employee ID, pay rate, rehire, separation.", flags: [] },
  { key: "originals", title: "Originals", desc: "Source scans exactly as received.", flags: [] },
];
// Classify an artifact into its Airtable group(s) by what it IS (title first, filing section as fallback).
// The backend annotates each doc with `groups` via the shared classifier — prefer it so the UI,
// compliance, and import always agree. A combined scan (e.g. ID + SS card) shows under both groups.
export function docGroupKeys(doc) {
  if (Array.isArray(doc.groups) && doc.groups.length) {
    // refine backend-annotated groups by title: an SS-card-titled doc goes to the ID card even if
    // it was annotated ss_i9 before the 2026-09 regrouping
    const t0 = (doc.title || "").toLowerCase();
    if (doc.groups.includes("ss_i9") && /social security|\bss card\b/.test(t0) && !/\bi-?9\b|verification/.test(t0)) {
      return doc.groups.map((k) => (k === "ss_i9" ? "id_passport" : k));
    }
    return doc.groups;
  }
  const t = (doc.title || "").toLowerCase();
  const sec = doc.section || "originals";
  const out = [];
  const add = (g) => { if (!out.includes(g)) out.push(g); };
  if (/driver'?s? licen|passport|photo id|\bid card\b|credentials/.test(t)) add("id_passport");
  // owner 2026-09-05: SS CARD files with ID/passport (card 1); I-9 + SS verification stay separate (card 2)
  if (/social security|\bss card\b/.test(t)) add("id_passport");
  if (/\bi-?9\b|ss verification/.test(t)) add("ss_i9");
  if (/\btb\b|tuberc/.test(t)) {
    if (/screen|risk.?assessment|education/.test(t)) add("tb_screening");
    if (/test|quantiferon|ppd|skin|chest x-?ray|laborator|clinical|gold/.test(t)) add("tb_testing");
    if (!out.includes("tb_screening") && !out.includes("tb_testing")) add("tb_testing");
  }
  if (/pennsylvania background|pa background|background check|background result|background clearance|state police|\bpa bg\b/.test(t)) add("pa_bg");
  if (/\bfbi\b|identogo|fingerprint/.test(t)) add("fbi");
  if (/residency|determination/.test(t)) add("residency");
  if (/drug|reference|application|interview|offer|pre-?screen/.test(t)) add("hiring");
  if (/orientation|onboarding/.test(t)) add("onboarding");
  if (/cpr|first aid|\bbls\b|competenc|training|quiz/.test(t)) add("certifications");
  if (out.length) return out;
  const bySection = {
    contact: "contact", hiring: "hiring", start_here: "hiring", onboarding: "onboarding",
    clearances: "id_passport", id_residency: "id_passport", id_passport: "id_passport",
    ss_i9: "ss_i9", tb: "tb_testing", health: "tb_testing", tb_testing: "tb_testing",
    tb_screening: "tb_screening", residency: "residency", background: "pa_bg", pa_bg: "pa_bg",
    fbi: "fbi", training: "certifications", certifications: "certifications",
    references: "hiring", employment: "employment",
  };
  return [bySection[sec] || "originals"];
}

// Expected document order within every section — the sequence the office expects to see.
// Drag-to-reorder (manual_order) always wins; then this canonical order; then date.
const GROUP_ORDER = {
  hiring: [/application/, /pre-?screen|interview/, /reference/, /drug/, /offer/],
  onboarding: [/hire|onboard/, /orientation/, /job description/],
  id_passport: [/driver'?s? licen|credentials|\bid\b/, /passport/, /photo id|\bid card\b/],
  ss_i9: [/social security|\bss card\b/, /\bi-?9\b/, /verification/],
  tb_testing: [/quantiferon|gold|blood|laborator/, /skin|ppd/, /chest x-?ray/],
  tb_screening: [/initial/, /screen|risk/, /annual/],
  residency: [/proof of residency|residency/, /determination/],
  pa_bg: [/background result|background check|pennsylvania|state police/, /attestation/, /review|determination/],
  fbi: [/receipt|identogo|fingerprint/, /fbi.*result|report/, /review/],
  certifications: [/competenc/, /cpr|first aid|\bbls\b/, /training|quiz/],
  employment: [/employee id|pay/, /rehire/, /separation|termination|resign/],
  contact: [],
  originals: [],
};

export function groupDocOrder(groupKey, title) {
  const t = (title || "").toLowerCase();
  const seq = GROUP_ORDER[groupKey] || [];
  for (let i = 0; i < seq.length; i++) if (seq[i].test(t)) return i * 10;
  return 500;
}

export function sortGroupDocs(groupKey, docs) {
  return [...docs].sort((a, b) => {
    const am = a.manual_order, bm = b.manual_order;
    if (am != null || bm != null) return (am == null ? 1 : bm == null ? -1 : am - bm);
    return groupDocOrder(groupKey, a.title) - groupDocOrder(groupKey, b.title)
      || String(a.doc_date || "").localeCompare(String(b.doc_date || ""));
  });
}

export function recordFields(emp, key) {  const p = emp.profile || {};
  const d = p.demographics || {};
  const uniq = (arr) => arr.filter((v, i, a) => v && a.indexOf(v) === i);
  const mdy = fmtDate;
  const kv = (obj) => obj && typeof obj === "object"
    ? Object.entries(obj).filter(([, v]) => v && typeof v !== "object").map(([k, v]) => [k.replace(/_/g, " "), mdy(String(v))])
    : [];
  switch (key) {
    case "contact": return [
      ["Status", p.contact_status],
      ["Date of birth", mdy(d.date_of_birth)],
      ["Phone", uniq([d.home_phone, d.cell_phone, d.phone, p.phone]).join(" · ")],
      ["Email", d.email || emp.email],
      ["Address", d.address && d.city && d.address.includes(d.city) ? d.address : ([d.address, d.city, d.state, d.zip].filter(Boolean).join(", ") || p.address)],
      ["Emergency contact", [p.emergency_contact?.name || p.emergency_contact_name, p.emergency_contact?.relationship, p.emergency_contact?.primary || p.emergency_contact_phone].filter(Boolean).join(" · ")],
      ["Application date", p.application_date],
    ];
    case "hiring": return [
      ["Application date", mdy(p.application_date)],
      ["Interview date", mdy(p.interview_date)],
      ["Could start", mdy(d.start_date)],
      ["Experience", d.experience_years && `${d.experience_years} yr(s)`],
      ["Eligible to work (US)", d.eligible_us],
      ["Driver's license + insurance", d.drivers_license],
      ["Reliable vehicle", d.reliable_vehicle],
      ...kv(p.drug_test),
      ...(emp.references || []).map((r) => [
        `Reference — ${r.name || ""}`,
        [r.relationship, r.satisfactory === true ? "satisfactory" : r.satisfactory === false ? "not satisfactory" : "", r.phone || r.email].filter(Boolean).join(" · "),
      ]),
    ];
    case "onboarding": return [
      ["Hire date", mdy(p.hire_date)],
      ["Rehire date", mdy(p.rehire_date)],
      ["First day worked (raw visits)", mdy(p.first_day_worked)],
      ["HHAeXchange first work date", mdy(p.hhae_first_work_date)],
      ["EVV utilization", p.evv_utilization],
      ["Last day worked", mdy(p.last_day_worked)],
      ["Termination date", mdy(p.termination_date)],
      ["Caregiver code", p.caregiver_code],
      ["Employee ID (EVV pin)", p.employee_id],
      ["Orientation completed", mdy((p.orientation_completed_at || "").slice(0, 10)) || ""],
    ];
    case "id_passport": return kv(p.id_document);
    case "ss_i9": return kv(p.i9);
    case "residency": return kv(p.residency);
    case "pa_bg": return [...kv(p.pa_bg), ...kv(p.attestations)];
    case "fbi": return kv(p.fbi);
    case "tb_testing": return kv(p.tb);
    case "tb_screening": return [["Last symptom & risk screen", mdy(p.tb?.screen_date)]];
    case "certifications": return [
      ["CPR / First Aid renew by", p.cpr_renew_by],
      ...(p.competency_scores || []).map((s) => [`Competency exam ${s.date}`, `${s.score}%`]),
    ];
    case "employment": return [
      ["Employee ID", p.employee_id && String(p.employee_id)],
      ["Pay rate", p.pay_rate && `$${p.pay_rate}`],
      ["Rehire eligible", p.rehire === true ? "Yes" : p.rehire === false ? "No" : ""],
      ["Separation", p.separation_date && [p.separation_type, p.separation_date].filter(Boolean).join(" · ")],
    ];
    default: return [];
  }
}

export function FileGroups({ file, emp, canUpload = false, onUpload, onOpenDoc, onReorder, onToggleComplete, onChanged, showEmpty = false, onlyKeys = null, skipKeys = [], bare = false }) {
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [moveFor, setMoveFor] = useState(null);
  if (!file || !emp) return null;
  const flags = file.compliance?.flags || [];
  const complete = file.sections_complete || {};
  const allDocs = Object.entries(file.sections || {}).flatMap(([sec, docs]) => (sec === "to_complete" ? [] : docs));

  const saveMeta = async (d, field, v) => {
    await api.patch(`/employee-file-docs/${d.id}`,
      field === "doc_date" ? { doc_date: toISODate(v) } : { details: { [field]: v } });
    onChanged?.();
  };

  const handleDrop = (g, targetId) => {
    if (!dragId) { setDragId(null); return; }
    const dragged = allDocs.find((d) => d.id === dragId);
    const groupDocs = sortGroupDocs(g.key, allDocs.filter((d) => docGroupKeys(d).includes(g.key)));
    // dropped into a DIFFERENT section -> move the document to that box
    if (dragged && !docGroupKeys(dragged).includes(g.key)) {
      setDragId(null);
      setDragOver(null);
      api.patch(`/employee-file-docs/${dragId}`, { group: g.key })
        .then(() => onChanged?.())
        .catch(() => {});
      return;
    }
    if (dragId === targetId) { setDragId(null); return; }
    const ids = groupDocs.map((d) => d.id);
    const from = ids.indexOf(dragId);
    if (from < 0) { setDragId(null); return; }
    const to = targetId ? ids.indexOf(targetId) : ids.length - 1;
    if (to < 0) { setDragId(null); return; }
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setDragId(null);
    setDragOver(null);
    onReorder?.(g.key, ids);
  };
  return (
    <>
      {GROUPS.filter((g) => g.key !== "tb_screening" && (onlyKeys ? onlyKeys.includes(g.key) : !skipKeys.includes(g.key))).map((g) => {
        const isTb = g.key === "tb_testing"; // one card holds BOTH TB testing (padded panel) and TB screening
        const gKeys = isTb ? ["tb_testing", "tb_screening"] : [g.key];
        const docs = sortGroupDocs(g.key, allDocs.filter((d) => docGroupKeys(d).some((k) => gKeys.includes(k))));
        const fields = [...recordFields(emp, g.key), ...(isTb ? recordFields(emp, "tb_screening") : [])].filter(([, v]) => v);
        const groupAll = flags.filter((f) => g.flags.includes(f.key));
        const okFlags = groupAll.filter((f) => f.status === "ok");
        const gflags = groupAll.filter((f) => f.status !== "ok");
        const autoComplete = groupAll.length > 0 && gflags.length === 0;
        const isComplete = Boolean(complete[g.key]) || autoComplete;
        if (!docs.length && !fields.length && !gflags.length && !canUpload && !showEmpty) return null;
        const tile = (d) => {
          const p = emp.profile || {};
          const keys = docGroupKeys(d);
          const idd = p.id_document || {};
          const tb = p.tb || {};
          const pa = p.pa_bg || {};
          const fbi = p.fbi || {};
          const tLow = (d.title || "").toLowerCase();
          // Date-driven documents always get their dates as FIELDS under the tile — never
          // buried in a wrapping title. The doc's own details win; record dates fill gaps.
          const meta = {
            number: d.details?.credential_no || (keys.includes("id_passport") ? idd.number : ""),
            issued: d.details?.issued || (keys.includes("id_passport") ? idd.issue : ""),
            expires: d.details?.expires
              || (keys.includes("id_passport") ? idd.expire : "")
              || (keys.includes("certifications") && /cpr|first aid|\bbls\b/.test(tLow) ? p.cpr_renew_by : ""),
            result: d.details?.result || (keys.includes("tb_testing") ? tb.result : ""),
          };
          const recordDate = (!d.doc_date && !meta.issued)
            ? (keys.includes("tb_testing") ? tb.date_read
              : keys.includes("tb_screening") ? tb.screen_date
              : keys.includes("pa_bg") ? pa.received
              : keys.includes("fbi") ? fbi.received : "")
            : "";
          return (
          <div key={d.id} onClick={() => onOpenDoc?.(d)} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter") onOpenDoc?.(d); }}
            draggable={canUpload}
            onDragStart={(e) => { e.dataTransfer.setData("text/plain", d.id); e.dataTransfer.effectAllowed = "move"; setDragId(d.id); }}
            onDragOver={(e) => { if (!canUpload) return; e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOver(d.id); }}
            onDragLeave={() => setDragOver((v) => (v === d.id ? null : v))}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(g, d.id); }}
            className={`group w-40 cursor-pointer rounded-lg text-left ${canUpload ? "cursor-grab active:cursor-grabbing" : ""} ${dragId && dragId !== d.id ? "opacity-70" : ""} ${dragOver === d.id ? "rounded-lg ring-2 ring-primary/60" : ""}`}
            data-testid={`doc-${d.id}`}>
            <div className="relative flex h-28 w-40 items-center justify-center overflow-hidden rounded-lg border border-stone-200 bg-stone-50 transition-colors group-hover:border-primary/50">
              {(d.content_type || "").startsWith("image/") || (d.content_type || "").includes("pdf") ? (
                <img src={`${api.defaults.baseURL}/employee-file-docs/${d.id}/thumb`} alt={d.title} draggable={false}
                  className="h-full w-full object-cover object-top" loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <FilePdf size={28} className="text-stone-300" />
              )}
              {canUpload && (
                <button title="Remove from file"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!window.confirm(`Remove "${d.title}" from the file? This cannot be undone.`)) return;
                    try {
                      await api.delete(`/employee-file-docs/${d.id}`);
                      toast.success("Removed from the file");
                      onChanged?.();
                    } catch (err) { toast.error(formatApiError(err)); }
                  }}
                  className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-stone-400 opacity-0 shadow-sm transition-opacity hover:text-[#C0392B] group-hover:opacity-100"
                  data-testid={`doc-delete-${d.id}`}>
                  <X size={12} weight="bold" />
                </button>
              )}
              {canUpload && (
                <button title="Move to another card"
                  onClick={(e) => { e.stopPropagation(); setMoveFor(moveFor === d.id ? null : d.id); }}
                  className="absolute left-1 top-1 rounded-full bg-white/90 p-1 text-stone-400 opacity-0 shadow-sm transition-opacity hover:text-primary group-hover:opacity-100"
                  data-testid={`doc-move-${d.id}`}>
                  <ArrowsLeftRight size={12} weight="bold" />
                </button>
              )}
              {moveFor === d.id && (
                <div className="absolute left-0 top-8 z-20 max-h-56 w-44 overflow-y-auto rounded-lg border border-stone-200 bg-white p-1 shadow-lg"
                  onClick={(e) => e.stopPropagation()} data-testid={`doc-move-menu-${d.id}`}>
                  <p className="px-2 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-stone-400">Move to…</p>
                  {GROUPS.filter((g2) => !docGroupKeys(d).includes(g2.key)).map((g2) => (
                    <button key={g2.key} type="button"
                      className="block w-full rounded px-2 py-1 text-left text-xs text-stone-600 hover:bg-stone-50"
                      data-testid={`doc-move-${d.id}-${g2.key}`}
                      onClick={async () => {
                        try {
                          await api.patch(`/employee-file-docs/${d.id}`, { group: g2.key });
                          toast.success(`Moved to ${g2.title}`);
                          setMoveFor(null);
                          onChanged?.();
                        } catch (err) { toast.error(formatApiError(err)); }
                      }}>
                      {g2.step ? `${g2.step} — ` : ""}{g2.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="mt-1 line-clamp-2 text-xs font-medium leading-snug text-stone-700">{d.title}</p>
            {d.needs_date && (
              <span className="mt-0.5 inline-block rounded-full border border-[#B7791F]/40 bg-[#B7791F]/10 px-2 py-0.5 text-[10px] font-semibold text-[#8a5d14]"
                data-testid={`doc-needs-date-${d.id}`}>needs date — on the form</span>
            )}
            {canUpload ? (
              <div className="mt-0.5 space-y-0.5" onClick={(e) => e.stopPropagation()} data-testid={`doc-meta-edit-${d.id}`}>
                <div className="flex items-center gap-1">
                  <DocMetaField value={fmtDate(d.doc_date || recordDate)} placeholder="date" testid={`doc-date-${d.id}`}
                    onSave={(v) => saveMeta(d, "doc_date", v)} />
                  {d.doc_date_source === "ocr" && <span className="shrink-0 rounded bg-[#B7791F]/15 px-1 text-[9px] font-bold uppercase text-[#8a5d14]">OCR</span>}
                  {d.doc_date_source === "import" && <span className="shrink-0 rounded bg-stone-200 px-1 text-[9px] font-bold uppercase text-stone-500">import</span>}
                  {d.doc_date_source === "manual" && <span className="shrink-0 rounded bg-[#27AE60]/15 px-1 text-[9px] font-bold uppercase text-[#1e8449]">entered</span>}
                </div>
                <div className="flex items-center gap-1">
                  <span className="shrink-0 text-[9px] font-bold uppercase text-stone-300">#</span>
                  <DocMetaField value={meta.number} placeholder="document no." testid={`doc-number-${d.id}`} tone="text-stone-600"
                    onSave={(v) => saveMeta(d, "credential_no", v)} />
                </div>
                <div className="flex items-center gap-1">
                  <span className="shrink-0 text-[9px] font-bold uppercase text-stone-300">issued</span>
                  <DocMetaField value={meta.issued} placeholder="—" testid={`doc-issued-${d.id}`}
                    onSave={(v) => saveMeta(d, "issued", v)} />
                </div>
                <div className="flex items-center gap-1">
                  <span className="shrink-0 text-[9px] font-bold uppercase text-stone-300">expires</span>
                  <DocMetaField value={meta.expires} placeholder="—" testid={`doc-expires-${d.id}`} tone="text-[#8a5d14]"
                    onSave={(v) => saveMeta(d, "expires", v)} />
                </div>
                {(meta.result || keys.includes("tb_testing")) && (
                  <div className="flex items-center gap-1">
                    <span className="shrink-0 text-[9px] font-bold uppercase text-stone-300">result</span>
                    <DocMetaField value={meta.result} placeholder="—" testid={`doc-result-${d.id}`} tone="text-stone-600"
                      onSave={(v) => saveMeta(d, "result", v)} />
                  </div>
                )}
              </div>
            ) : (
              <>
                {meta.number && <p className="truncate text-[11px] font-medium text-stone-600" data-testid={`doc-number-${d.id}`}># {meta.number}</p>}
                {d.doc_date && (
                  <p className="text-[11px] font-medium text-stone-500" data-testid={`doc-date-${d.id}`}>
                    {d.doc_date}
                    {d.doc_date_source === "ocr" && <span className="ml-1 rounded bg-[#B7791F]/15 px-1 text-[9px] font-bold uppercase text-[#8a5d14]">OCR</span>}
                    {d.doc_date_source === "import" && <span className="ml-1 rounded bg-stone-200 px-1 text-[9px] font-bold uppercase text-stone-500">import</span>}
                    {d.doc_date_source === "manual" && <span className="ml-1 rounded bg-[#27AE60]/15 px-1 text-[9px] font-bold uppercase text-[#1e8449]">entered</span>}
                  </p>
                )}
                {recordDate && (
                  <p className="text-[11px] font-medium text-stone-500" data-testid={`doc-record-date-${d.id}`}>
                    {recordDate} <span className="rounded bg-stone-200 px-1 text-[9px] font-bold uppercase text-stone-500">record</span>
                  </p>
                )}
                {meta.issued && <p className="text-[10px] text-stone-500" data-testid={`doc-issued-${d.id}`}>issued {meta.issued}</p>}
                {meta.expires && <p className="text-[10px] text-[#8a5d14]" data-testid={`doc-expires-${d.id}`}>expires {meta.expires}</p>}
                {meta.result && <p className="text-[10px] text-stone-600" data-testid={`doc-result-${d.id}`}>result: {meta.result}</p>}
              </>
            )}
          </div>
          );
        };
        return (
          <div key={g.key} className={bare ? "" : "rounded-xl border border-stone-200 bg-white p-6 shadow-sm"} data-testid={`file-group-${g.key}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h2 className="font-heading text-lg font-semibold text-stone-900">
                  {g.step && <span className="mr-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#4A235A] align-middle text-xs font-bold text-white" data-testid={`group-step-${g.key}`}>{g.step}</span>}
                  {g.title} <span className="text-xs font-normal text-stone-400">({docs.length})</span></h2>
                <p className="text-xs text-stone-500">{g.desc}</p>
              </div>
              <span className="flex shrink-0 items-center gap-2">
                {isComplete && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-[#27AE60]/40 bg-[#27AE60]/5 px-2.5 py-1 text-[11px] font-semibold text-[#1e8449]" data-testid={`section-complete-${g.key}`}>
                    <CheckCircle size={12} weight="fill" /> Complete
                  </span>
                )}
                {canUpload && !autoComplete && (
                  <Button size="sm" variant="outline" onClick={() => onToggleComplete?.(g.key, !complete[g.key])}
                    className="rounded-full border-stone-300 text-xs" data-testid={`complete-toggle-${g.key}`}>
                    {complete[g.key] ? "Reopen" : "Mark complete"}
                  </Button>
                )}
                {canUpload && (
                  <Button size="sm" variant="outline" onClick={() => onUpload?.(g.key)}
                    className="rounded-full border-stone-300 text-xs" data-testid={`upload-${g.key}`}>
                    <UploadSimple size={13} className="mr-1" /> Add document
                  </Button>
                )}
              </span>
            </div>
            {groupAll.length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid={`group-flags-${g.key}`}>
                {okFlags.map((f) => (
                  <p key={f.key} className="flex items-start gap-1.5 text-[12px] text-stone-600" data-testid={`flag-ok-${f.key}`}>
                    <CheckCircle size={14} weight="fill" className="mt-0.5 shrink-0 text-[#1e8449]" />
                    <span><span className="font-medium text-stone-700">{f.label}</span>{f.due ? <span className="text-stone-400"> · current through {f.due}</span> : ""}</span>
                  </p>
                ))}
                {gflags.map((f) => (
                  <p key={f.key} title={f.detail}
                    className={`flex items-start gap-1.5 text-[12px] ${f.severity === "warn" ? "text-[#8a5d14]" : "text-[#C0392B]"}`}
                    data-testid={`group-flag-${f.key}`}>
                    <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0" />
                    <span><span className="font-semibold">{f.label}</span>{f.detail ? ` — ${f.detail}` : ""}</span>
                  </p>
                ))}
              </div>
            )}
            {fields.length > 0 && (
              <div className="mt-3 grid grid-cols-1 gap-x-8 gap-y-1.5 sm:grid-cols-2" data-testid={`group-fields-${g.key}`}>
                {fields.map(([label, value]) => (
                  <p key={label} className="text-sm">
                    <span className="capitalize text-stone-400">{label}: </span>
                    <span className="font-medium text-stone-800">{value}</span>
                  </p>
                ))}
              </div>
            )}
            {docs.length > 0 && (
              isTb ? (
              <div className="mt-4 space-y-3" data-testid={`group-docs-${g.key}`}>
                {[["tb_testing", "TB testing — the test itself", "QuantiFERON, skin test, or chest x-ray."],
                  ["tb_screening", "TB screening — symptom & risk screens", "Initial and annual screens."]].map(([sub, label, subDesc]) => {
                  const subDocs = docs.filter((d) => docGroupKeys(d).includes(sub));
                  if (!subDocs.length) return null;
                  return (
                    <div key={sub}
                      className={`rounded-lg border p-4 ${sub === "tb_testing" ? "border-[#4A235A]/30 bg-[#4A235A]/[0.04]" : "border-stone-200 bg-stone-50/60"}`}
                      data-testid={`tb-panel-${sub}`}
                      onDragOver={(e) => { if (canUpload) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } }}
                      onDrop={(e) => { if (!canUpload) return; e.preventDefault(); handleDrop(g, subDocs[subDocs.length - 1]?.id); }}>
                      <p className={`mb-3 text-[11px] font-bold uppercase tracking-wider ${sub === "tb_testing" ? "text-[#4A235A]" : "text-stone-500"}`}>{label}</p>
                      <div className="flex flex-wrap gap-3">{subDocs.map((d) => tile(d))}</div>
                    </div>
                  );
                })}
              </div>
              ) : (
              <div className={`flex flex-wrap gap-3 ${fields.length > 0 || gflags.length > 0 ? "mt-4 border-t border-stone-100 pt-4" : "mt-4"} ${dragId && canUpload ? "rounded-lg outline-dashed outline-1 outline-stone-300" : ""}`} data-testid={`group-docs-${g.key}`}
                onDragOver={(e) => { if (canUpload) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } }}
                onDrop={(e) => { if (!canUpload) return; e.preventDefault(); handleDrop(g, docs[docs.length - 1]?.id); }}>
                {docs.map((d) => tile(d))}
              </div>
              )
            )}
            {docs.length === 0 && fields.length === 0 && gflags.length === 0 && (canUpload || showEmpty) && (
              <p className="mt-3 text-xs italic text-stone-300">Nothing filed here yet.</p>
            )}
          </div>
        );
      })}
    </>
  );
}
